import * as React from "react";
import { Slot } from "radix-ui";

/**
 * The 17-step type ramp from the design system (section 02). Nothing in HourSign uses a
 * size that is not on this list - if a design seems to need one, the step above or below
 * is the answer. Each component is a thin wrapper over its `text-*` utility in tokens.css.
 *
 * `asChild` swaps the element but keeps the class, so `<H1 asChild><Link/></H1>` works.
 */
type TypographyProps = React.ComponentProps<"p"> & { asChild?: boolean };

// ponytail: one factory, 17 calls. Seventeen hand-written near-identical components is
// the thing this replaces.
//
// The step class is joined, not passed through cn(). tailwind-merge reads `text-h1` and
// `text-mono` as colour utilities, so cn("text-h1", "text-foreground") returns only
// "text-foreground" and the whole ramp silently disappears. The real fix is a
// tailwind-merge classGroup in @/lib/utils - handed off. Until it lands, joining is
// correct anyway: a step sets size/line/weight, a colour class sets colour, and the two
// never touch the same property. A caller passing a second step is already breaking
// rule 7 ("type only from the ramp"), so there is nothing worth de-duplicating.
function step(utility: string, defaultTag: React.ElementType, displayName: string) {
  function Step({ className, asChild = false, ...props }: TypographyProps) {
    const Comp = asChild ? Slot.Root : defaultTag;
    const classes = className ? `${utility} ${className}` : utility;
    return <Comp data-slot={displayName} className={classes} {...props} />;
  }
  Step.displayName = displayName;
  return Step;
}

export const DisplayXl = step("text-display-xl", "div", "display-xl");
export const Display = step("text-display", "div", "display");
export const H1 = step("text-h1", "h1", "h1");
export const H2 = step("text-h2", "h2", "h2");
export const H3 = step("text-h3", "h3", "h3");
export const H4 = step("text-h4", "h4", "h4");
export const BodyLg = step("text-body-lg", "p", "body-lg");
export const Body = step("text-body", "p", "body");
export const Label = step("text-label", "span", "label");
export const BodySm = step("text-body-sm", "p", "body-sm");
export const Caption = step("text-caption", "p", "caption");
export const Overline = step("text-overline", "span", "overline");
export const MonoLg = step("text-mono-lg", "span", "mono-lg");
export const Mono = step("text-mono", "span", "mono");
export const MonoSm = step("text-mono-sm", "code", "mono-sm");
export const Signature = step("text-signature", "span", "signature");
export const Attestation = step("text-attestation", "p", "attestation");

export type { TypographyProps };
